import type { Holiday } from '@clockwise/clients/holidays';
import { ApiError, isResolvableCountryInput, isValidIsoDate } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { COUNTRY_FIELD_ERROR, CountrySelectField } from '@/components/custom/country-select';
import { RequirePermission } from '@/components/custom/require-permission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { holidaysApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Add / edit / delete a holiday, in one side drawer.
 *
 * Unlike activities — which have a full detail route because they carry
 * relationships worth a page of their own — a holiday is four fields with no
 * children, so the drawer IS the detail view. Delete lives here too, next to
 * the data it removes.
 */

// Shape-level only. Uniqueness of (country, date, holiday) is a business rule
// the server owns; its ValidationError lands inline on `name` via `setError`.
const HolidayFormSchema = z.object({
  country: z
    .string()
    .min(1, COUNTRY_FIELD_ERROR)
    .refine(isResolvableCountryInput, COUNTRY_FIELD_ERROR),
  date: z.string().min(1, 'Pick a date').refine(isValidIsoDate, 'Pick a valid date'),
  name: z.string().trim().min(1, 'Required').max(200, 'Up to 200 characters'),
  description: z.string().max(2000, 'Up to 2000 characters'),
});
type HolidayFormValues = z.infer<typeof HolidayFormSchema>;

const blankValues: HolidayFormValues = { country: '', date: '', name: '', description: '' };

interface HolidayFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row being edited, or `null` to add a new holiday. */
  holiday: Holiday | null;
}

export function HolidayFormSheet({ open, onOpenChange, holiday }: HolidayFormSheetProps) {
  const isEdit = holiday !== null;
  const queryClient = useQueryClient();

  const form = useForm<HolidayFormValues>({
    resolver: zodResolver(HolidayFormSchema),
    defaultValues: blankValues,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      holiday
        ? {
            country: holiday.country,
            date: holiday.date,
            name: holiday.name,
            description: holiday.description ?? '',
          }
        : blankValues,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, holiday?.id]);

  const invalidate = () => {
    // `holidays.all` is the prefix for the list AND the country filter — a new
    // country must appear in the dropdown immediately.
    queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const saveMutation = useMutation({
    mutationFn: (values: HolidayFormValues) => {
      const body = {
        country: values.country,
        date: values.date,
        name: values.name.trim(),
        description: values.description.trim() || null,
      };
      return isEdit && holiday ? holidaysApi.patch(holiday.id, body) : holidaysApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Holiday updated' : 'Holiday added');
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => applyFieldErrors(form, err),
  });

  // Delete only. It is a SOFT delete server-side — the row stays for audit and
  // for historical working-day calculations — but the calendar only ever lists
  // live holidays, so there is no UI path back. Restoring is an API operation
  // (POST /api/holidays/:id/restore).
  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!holiday) throw new Error('No holiday selected');
      return holidaysApi.remove(holiday.id);
    },
    onSuccess: () => {
      toast.success('Holiday deleted');
      invalidate();
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete this holiday');
    },
  });

  const busy = saveMutation.isPending || deleteMutation.isPending;
  const onSubmit = form.handleSubmit((v) => saveMutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: busy,
    onOpenChange,
  });

  const dateError = form.formState.errors.date?.message;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit holiday' : 'Add holiday'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Changes apply immediately.'
                : 'One country, one date. Add a row per year — the importer loads a whole year at once.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-4">
              <CountrySelectField form={form} name="country" id="holiday-country" disabled={busy} />

              <div className="space-y-1.5">
                <Label htmlFor="holiday-date">Date</Label>
                <DatePicker
                  id="holiday-date"
                  value={form.watch('date')}
                  onChange={(v) =>
                    form.setValue('date', v, { shouldValidate: true, shouldDirty: true })
                  }
                  disabled={busy}
                  ariaInvalid={Boolean(dateError)}
                />
                {dateError ? <p className="text-xs text-destructive">{dateError}</p> : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="holiday-name">Holiday</Label>
                <Input
                  id="holiday-name"
                  placeholder="Christmas Day"
                  disabled={busy}
                  aria-invalid={Boolean(form.formState.errors.name)}
                  {...form.register('name')}
                />
                {form.formState.errors.name ? (
                  <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="holiday-description">Description</Label>
                <Textarea
                  id="holiday-description"
                  rows={3}
                  placeholder="Optional — e.g. observed on the following Monday"
                  disabled={busy}
                  {...form.register('description')}
                />
                {form.formState.errors.description ? (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.description.message}
                  </p>
                ) : null}
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <div>
                {isEdit && holiday ? (
                  <RequirePermission permission="holiday.edit">
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      disabled={busy}
                      onClick={() => {
                        if (
                          confirm(
                            `Delete "${holiday.name}"? It will stop counting as a non-working day and leave this calendar.`,
                          )
                        ) {
                          deleteMutation.mutate();
                        }
                      }}
                    >
                      <Trash2 />
                      Delete
                    </Button>
                  </RequirePermission>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Add holiday'}
                </Button>
              </div>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}

/**
 * Route the server's field errors to the matching inputs (G12). The duplicate
 * check comes back on `name`, which is the field the user has to change —
 * country and date are legitimately shared by sibling observances.
 *
 * Anything without a field path is a genuine non-form failure and gets a
 * toast; it must never be swallowed (G15).
 */
function applyFieldErrors(form: UseFormReturn<HolidayFormValues>, err: unknown) {
  const fields = err instanceof ApiError ? err.fields : undefined;
  let matched = false;
  for (const f of fields ?? []) {
    if (
      f.path === 'country' ||
      f.path === 'date' ||
      f.path === 'name' ||
      f.path === 'description'
    ) {
      form.setError(f.path, { message: f.message });
      matched = true;
    }
  }
  if (!matched) {
    toast.error(err instanceof ApiError ? err.message : 'Could not save this holiday');
  }
}
